import { useState } from 'react'
import BugTable from './BugTable'
import type { z } from 'zod'
import { findingSessionCount, type SummaryRunSchema, type SummaryBugSchema } from '../schemas/sessionSummarizers'
import { Link } from './nav'

type Run = z.infer<typeof SummaryRunSchema>
type Bug = z.infer<typeof SummaryBugSchema>

function BugList({ bugs, projectId, paginate = false }: { bugs: Bug[]; projectId: string; paginate?: boolean }) {
  const [visibleCount, setVisibleCount] = useState(5)
  const rows = bugs
  return <><BugTable bugs={paginate ? rows.slice(0, visibleCount) : rows} projectId={projectId}
    loading={false} hasWebhook={false} showEnvironment={false} showPullRequests renderDetails={bug => {
      const evidence = bugs.find(item => item.id === bug.id)!
      const count = evidence.impacted_sessions
      return <p className="summary-result__bug-evidence">
        <strong>{count === null ? 'Session count unavailable' : `${count} ${count === 1 ? 'session' : 'sessions'}`}</strong>
        <span className="summary-result__muted"> · {evidence.is_duplicate ? 'Duplicate' : 'New bug'} · Opened {evidence.opened_at.slice(0, 10)}</span>
      </p>
    }} />
    {paginate && rows.length > visibleCount && <button type="button" className="summary-result__load-more" onClick={() => setVisibleCount(count => count + 5)}>Show more bugs</button>}
  </>
}

export default function SessionSummaryResult({ run, projectId }: { run: Run; projectId: string }) {
  if (!run.output) return null

  const openBugs = run.bugs.filter(bug => bug.status === 'open')
  const newBugs = openBugs.filter(bug => !bug.is_duplicate)
  const duplicateBugs = openBugs.filter(bug => bug.is_duplicate)

  const userTrends = run.output.findings.filter(f => f.category === 'User trends')
  const friction = run.output.findings.filter(f => f.category === 'Friction')

  const frictionWithCounts = friction.map(f => ({
    finding: f,
    count: findingSessionCount(f, run.review_sessions, run.bugs),
    title: f.title || f.text.split(/[.!?](?:\s|$)/)[0] || 'Friction',
  })).sort((a, b) => (b.count ?? 0) - (a.count ?? 0))

  const maxFrictionCount = Math.max(...frictionWithCounts.map(f => f.count ?? 0), 1)

  const frictionSessionCount = new Set(
    friction.flatMap(f => {
      const supporting = new Set(f.supporting_review_ids ?? f.source_ids)
      return run.review_sessions.filter(r => supporting.has(r.id)).map(r => r.session_id)
    })
  ).size

  const overviewLines = run.output.overview.split(/(?<=[.!?])\s+/)
  const headline = overviewLines[0] || run.output.overview
  const overviewBody = overviewLines.length > 1 ? overviewLines.slice(1).join(' ') : ''

  return <div className="summary-result">
    {/* Hero: Overview */}
    <section className="summary-result__hero">
      <span className="summary-result__eyebrow">The day</span>
      <h3 className="summary-result__headline">{headline}</h3>
      {overviewBody && <p className="summary-result__overview-body">{overviewBody}</p>}
    </section>

    {/* Quick stats */}
    <div className="summary-result__stats">
      <div className="summary-result__stat">
        <span className="summary-result__stat-value">{(run.reviewed_sessions ?? 0).toLocaleString()}</span>
        <span className="summary-result__stat-label">sessions reviewed</span>
      </div>
      <div className="summary-result__stat">
        <span className="summary-result__stat-value" style={frictionSessionCount > 0 ? { color: 'var(--color-severity-medium)' } : undefined}>{frictionSessionCount || '—'}</span>
        <span className="summary-result__stat-label">with friction</span>
      </div>
      <div className="summary-result__stat">
        <span className="summary-result__stat-value" style={newBugs.length > 0 ? { color: 'var(--color-danger)' } : undefined}>{newBugs.length}</span>
        <span className="summary-result__stat-label">{newBugs.length === 1 ? 'bug filed' : 'bugs filed'}</span>
      </div>
      <div className="summary-result__stat">
        <span className="summary-result__stat-value">{duplicateBugs.length}</span>
        <span className="summary-result__stat-label">{duplicateBugs.length === 1 ? 'duplicate bug detected' : 'duplicate bugs detected'}</span>
      </div>
    </div>

    {/* Two-column findings dashboard */}
    {(userTrends.length > 0 || friction.length > 0) && <div className="summary-result__dashboard">
      {/* Left: What users came to do */}
      {userTrends.length > 0 && <section className="summary-result__panel">
        <div className="summary-result__panel-header"><h5>What users came to do</h5></div>
        <div className="summary-result__finding-list">
          {userTrends.map((finding, index) => {
            const sessionCount = findingSessionCount(finding, run.review_sessions, run.bugs)
            const fallbackTitle = finding.text.split(/[.!?](?:\s|$)/)[0] || 'User trend'
            const title = finding.title || (fallbackTitle.length > 120 ? `${fallbackTitle.slice(0, 117)}…` : fallbackTitle)
            const reviews = run.sources.filter(source => source.kind === 'review' && finding.source_ids.includes(source.id))
            return <details key={`${run.id}:trends:${index}`} className="summary-result__finding">
              <summary className="summary-result__finding-toggle">
                <div className="summary-result__finding-info">
                  <span className="summary-result__finding-title">{title}</span>
                  <span className="summary-result__finding-meta">
                    {sessionCount === null ? 'Session count unavailable' : `${sessionCount} ${sessionCount === 1 ? 'session' : 'sessions'}`}
                  </span>
                </div>
              </summary>
              <div className="summary-result__finding-detail">
                <p className="summary-result__prose">{finding.text}</p>
                {reviews.length > 0 && <>
                  <h6 className="summary-result__detail-heading">Supporting reviews</h6>
                  <div className="summary-result__evidence-list">{reviews.map(source => {
                    if (source.kind !== 'review') return null
                    const evidence = run.review_evidence.find(review => review.id === source.id)
                    return <details key={source.id} className="summary-result__evidence">
                      <summary>{evidence?.user_email?.trim() || 'Anonymous'} <span className="summary-result__muted">· {source.reviewer}</span></summary>
                      <div className="summary-result__evidence-body">
                        <div className="summary-result__evidence-links">
                          {evidence?.session_url && /^https?:\/\//i.test(evidence.session_url) && <a href={evidence.session_url} target="_blank" rel="noopener noreferrer">Open FullStory session</a>}
                          {evidence?.journeys.map(journey => <Link key={journey.id} to={`/projects/${projectId}/journeys/${encodeURIComponent(journey.id)}`}>{journey.name || 'Open journey'}</Link>)}
                          <Link to={`/projects/${projectId}/session-reviewers/${encodeURIComponent(source.reviewer)}`}>Open reviewer</Link>
                        </div>
                        <ul className="summary-result__observation-list">{source.output.observations.map((observation, i) => <li key={i}>{observation.description}</li>)}</ul>
                      </div>
                    </details>
                  })}</div>
                </>}
                {!reviews.length && <p className="summary-result__muted">No reviews cited.</p>}
              </div>
            </details>
          })}
        </div>
      </section>}

      {/* Right: Where it broke */}
      {friction.length > 0 && <section className="summary-result__panel">
        <div className="summary-result__panel-header"><h5>Where it broke</h5></div>
        <div className="summary-result__ranked-list">
          {frictionWithCounts.map((item, index) => {
            const width = item.count !== null && maxFrictionCount > 0 ? Math.max((item.count / maxFrictionCount) * 100, 4) : 0
            const reviews = run.sources.filter(source => source.kind === 'review' && item.finding.source_ids.includes(source.id))
            return <details key={`${run.id}:bar:${index}`} className="summary-result__ranked-item summary-result__ranked-item--expandable">
              <summary className="summary-result__ranked-toggle">
                <div className="summary-result__ranked-header">
                  <span className="summary-result__ranked-rank">{index + 1}.</span>
                  <span className="summary-result__ranked-title">{item.title}</span>
                  {item.count !== null && <span className="summary-result__ranked-count">{item.count}</span>}
                </div>
                {item.count !== null && <div className="summary-result__ranked-track">
                  <div className="summary-result__ranked-fill" style={{ width: `${width}%` }} />
                </div>}
              </summary>
              <div className="summary-result__finding-detail">
                <p className="summary-result__prose">{item.finding.text}</p>
                {reviews.length > 0 && <>
                  <h6 className="summary-result__detail-heading">Supporting reviews</h6>
                  <div className="summary-result__evidence-list">{reviews.map(source => {
                    if (source.kind !== 'review') return null
                    const evidence = run.review_evidence.find(review => review.id === source.id)
                    return <details key={source.id} className="summary-result__evidence">
                      <summary>{evidence?.user_email?.trim() || 'Anonymous'} <span className="summary-result__muted">· {source.reviewer}</span></summary>
                      <div className="summary-result__evidence-body">
                        <div className="summary-result__evidence-links">
                          {evidence?.session_url && /^https?:\/\//i.test(evidence.session_url) && <a href={evidence.session_url} target="_blank" rel="noopener noreferrer">Open FullStory session</a>}
                          {evidence?.journeys.map(journey => <Link key={journey.id} to={`/projects/${projectId}/journeys/${encodeURIComponent(journey.id)}`}>{journey.name || 'Open journey'}</Link>)}
                          <Link to={`/projects/${projectId}/session-reviewers/${encodeURIComponent(source.reviewer)}`}>Open reviewer</Link>
                        </div>
                        <ul className="summary-result__observation-list">{source.output.observations.map((observation, i) => <li key={i}>{observation.description}</li>)}</ul>
                      </div>
                    </details>
                  })}</div>
                </>}
                {!reviews.length && <p className="summary-result__muted">No reviews cited.</p>}
              </div>
            </details>
          })}
        </div>
      </section>}
    </div>}

    {/* New bugs table */}
    <section className="summary-result__bugs-section">
      <h4>{`New bugs (${newBugs.length})`}</h4>
      {newBugs.length ? <BugList key={`${run.id}:${run.completed_at}:new`} bugs={newBugs} projectId={projectId} paginate /> : <p>No new bugs.</p>}
    </section>

    {/* Duplicate bugs table */}
    <section className="summary-result__bugs-section">
      <h4>{`Duplicate bugs (${duplicateBugs.length})`}</h4>
      {duplicateBugs.length ? <BugList key={`${run.id}:${run.completed_at}:duplicates`} bugs={duplicateBugs} projectId={projectId} paginate /> : <p>No duplicate bugs.</p>}
    </section>

    {/* Coverage footer */}
    {run.cutoff && <footer className="summary-result__coverage">
      <span className="summary-result__coverage-label">Coverage &amp; caveats</span>
      <span>{run.reviewed_sessions ?? 0} of {run.sessions ?? '?'} sessions reviewed{run.completed_at && <> · Generated <time dateTime={new Date(run.completed_at).toISOString()}>{new Date(run.completed_at).toLocaleString()}</time></>}</span>
    </footer>}
  </div>
}
